/**
 * What the passenger sees.
 *
 * A flight information display is not a smaller operations board. It is a
 * DERIVED view of the same record, in a different vocabulary, for people who
 * cannot ask a follow-up question — and almost everything that makes it
 * trustworthy is a rule about what it must not say.
 *
 * ── the status is derived, never typed ──────────────────────────────────
 *
 * The moment somebody can set a FIDS status by hand, the board and the
 * operation disagree, and the board is the one twelve thousand people are
 * reading. So there is no setter here. Every status below is a function of
 * the movement's own times, its stand and its cancellation — change the
 * record and the board changes with it.
 *
 * ── never show a stale time as a promise ────────────────────────────────
 *
 * The worst thing a display can do is keep showing a departure time that has
 * passed. A passenger reads "14:20" at 14:40 and concludes the board is
 * broken, or worse, that they have missed the aircraft. A movement that is
 * late with no new time shows **Delayed** and NO TIME AT ALL — which is the
 * truth, and which sends people to ask rather than to sit.
 *
 * ── a gate is not a stand ───────────────────────────────────────────────
 *
 * The operation allocates stands; passengers walk to gates. A remote stand
 * has no gate and the board must say where the bus goes rather than print a
 * stand number nobody can find. A contact stand with no gate recorded shows
 * nothing rather than guessing.
 */
import { t } from "../i18n";
import { bestKnown, delayMinutes, flightName, type Flight } from "./flights";
import type { StandRegister } from "./stands";

export type FidsStatus =
  | "scheduled"
  | "on_time"
  | "delayed"
  | "boarding"
  | "final_call"
  | "gate_closed"
  | "departed"
  | "landed"
  | "arrived"
  | "cancelled"
  | "see_staff";

export const FIDS_LABEL: Record<FidsStatus, string> = {
  scheduled: "Scheduled",
  on_time: "On time",
  delayed: "Delayed",
  boarding: "Boarding",
  final_call: "Final call",
  gate_closed: "Gate closed",
  departed: "Departed",
  landed: "Landed",
  arrived: "Arrived",
  cancelled: "Canceled",
  see_staff: "Please ask staff",
};

/** Minutes before a departure that boarding is shown. PROPOSED. */
export const BOARDING_FROM = 40;
export const FINAL_CALL_FROM = 20;
export const GATE_CLOSED_FROM = 10;

export interface FidsRow {
  flightId: string;
  flight: string;
  carrier?: string;
  direction: Flight["direction"];
  /** origin for an arrival, destination for a departure */
  place: string;
  scheduled: string | null;
  /**
   * The time shown to the public, and nothing when there is nothing honest to
   * show. A late movement with no new estimate has no time on the board.
   */
  shown: string | null;
  status: FidsStatus;
  /** where to go: a gate, or where the bus leaves from */
  gate?: string;
  /** said when the status alone is not enough — a cancellation's reason is NOT public */
  note?: string;
}

function gateFor(flight: Flight, register?: StandRegister, all?: readonly Flight[]): { gate?: string; note?: string } {
  /* the gate the allocation gave this departure — a bus gate or a moved gate is the flight's, not the stand's */
  if (flight.gate) return { gate: flight.gate };
  /*
   * A departure usually has no stand assignment of its own: the aircraft is
   * on the stand it arrived at, and the plan records that once, against the
   * arrival. So the gate is looked up through the turnaround — without this
   * every departure on the board showed no gate at all, which is the one
   * column a passenger is actually looking for.
   */
  const paired =
    flight.turnaround && all
      ? all.find((f) => f.turnaround === flight.turnaround && f.id !== flight.id)
      : undefined;
  const assignment =
    register?.assignments.find((a) => a.flightId === flight.id) ??
    (paired ? register?.assignments.find((a) => a.flightId === paired.id) : undefined);
  if (!assignment) return {};
  const stand = register?.stands.find((s) => s.code === assignment.standCode);
  if (!stand) return {};
  if (stand.gate) return { gate: stand.gate };
  /* a remote stand has no gate: say where the bus goes, not a stand number */
  if (stand.kind === "remote") return { note: t("by bus from the gate") };
  return {};
}

/**
 * The public status of one movement.
 *
 * Read the order carefully: cancellation first, because a cancelled flight
 * that has an estimated time would otherwise show as delayed; then what has
 * actually happened; then boarding, which is a function of the clock and the
 * gate; then the delay.
 */
/** How long a board keeps saying "delayed" before it sends people to a person. PROPOSED. */
export const SILENCE_BEFORE_STAFF = 45;

export function fidsStatus(flight: Flight, now: Date, hasGate: boolean): FidsStatus {
  if (flight.cancelled) return "cancelled";
  if (flight.actualAt) return flight.direction === "arrival" ? "landed" : "departed";

  const delay = delayMinutes(flight);
  const when = bestKnown(flight);
  const at = when.at ? Date.parse(when.at) : null;
  const scheduled = flight.scheduledAt ? Date.parse(flight.scheduledAt) : null;

  if (flight.direction === "departure" && at !== null && hasGate) {
    const minutesAway = (at - now.getTime()) / 60_000;
    if (minutesAway <= GATE_CLOSED_FROM && minutesAway > -15) return "gate_closed";
    if (minutesAway <= FINAL_CALL_FROM) return "final_call";
    if (minutesAway <= BOARDING_FROM && minutesAway > 0) return "boarding";
  }

  /* a new time, later than the schedule: delayed, and the board can say until when */
  if (delay !== null && delay > 15) return "delayed";

  /*
   * Its time has come and gone with nothing recorded.
   *
   * This is the commonest state on a real board and the one a naive model
   * cannot express: the operator knows the flight is late and does not yet
   * know by how much. It is DELAYED — with no time, because there is no
   * honest one — and after a long enough silence it stops pretending the
   * board is any use and sends the passenger to a person.
   */
  if (scheduled !== null && scheduled < now.getTime()) {
    const silent = (now.getTime() - scheduled) / 60_000;
    return silent > SILENCE_BEFORE_STAFF ? "see_staff" : "delayed";
  }
  if (delay !== null) return "on_time";
  return "scheduled";
}

/**
 * The board.
 *
 * `shown` is the time the public gets, and it is deliberately absent for a
 * delayed movement with no new estimate: a board that keeps a passed time on
 * screen is worse than one that admits it does not know yet.
 */
export function fidsBoard(
  flights: readonly Flight[],
  now: Date,
  direction: Flight["direction"],
  register?: StandRegister,
  windowHours = 6,
): FidsRow[] {
  const to = now.getTime() + windowHours * 3_600_000;
  const settled = now.getTime() - 45 * 60_000;
  return flights
    .filter((f) => f.direction === direction && f.source !== "forecast")
    .filter((f) => {
      const at = bestKnown(f).at;
      if (!at) return false;
      const ms = Date.parse(at);
      if (ms > to) return false;
      /*
       * A movement that has NOT happened stays on the board however late it
       * is. Dropping it after an hour takes it off the screen at exactly the
       * moment the people waiting for it most need to see it — and they are
       * standing in the hall whether or not the board has given up. What
       * comes off is what has settled: departed, landed, or cancelled long
       * enough ago that nobody is still arriving for it.
       */
      if (!f.actualAt && !f.cancelled) return true;
      return ms >= settled;
    })
    .map((f) => {
      const where = gateFor(f, register, flights);
      const status = fidsStatus(f, now, Boolean(where.gate));
      const estimate = f.actualAt ?? f.estimatedAt ?? null;
      /*
       * A delayed movement shows a time only when somebody has entered a NEW
       * one. An estimate equal to the schedule is not a new time, it is the
       * schedule repeated, and putting it in the "expected" column tells a
       * passenger the flight is running to time when it is not.
       */
      const hasNewTime = Boolean(estimate && f.scheduledAt && estimate !== f.scheduledAt);
      return {
        flightId: f.id,
        flight: flightName(f),
        carrier: f.carrier,
        direction: f.direction,
        place: f.otherAirport ?? t("—"),
        scheduled: f.scheduledAt ?? null,
        shown: status === "cancelled" ? null : status === "delayed" && !hasNewTime ? null : estimate,
        status,
        gate: where.gate,
        note: f.cancelled ? undefined : where.note,
      } satisfies FidsRow;
    })
    .sort((a, b) => (a.scheduled ?? "").localeCompare(b.scheduled ?? ""));
}

/**
 * What the board is not saying, for the operator looking at it.
 *
 * A display is judged by a passenger on whether it helped. This is the same
 * board read back as a list of the places it could not help — the movements
 * with no time, no gate, or nothing but "ask staff". It is not shown to the
 * public; it is what somebody fixes before the hall fills up.
 */
export interface FidsGaps {
  noTime: FidsRow[];
  noGate: FidsRow[];
  askStaff: FidsRow[];
}

export function fidsGaps(rows: readonly FidsRow[]): FidsGaps {
  return {
    noTime: rows.filter((r) => r.shown === null && r.status !== "cancelled"),
    noGate: rows.filter((r) => r.direction === "departure" && !r.gate && !r.note && r.status !== "cancelled"),
    askStaff: rows.filter((r) => r.status === "see_staff"),
  };
}
